/**
 * FilterSelect — durchsuchbares Auswahlfeld (Combobox) als Ersatz für native
 * Selects mit vielen Optionen (Tester-Meldung #12 / Issue #3).
 *
 * Muster: EmployeePicker (WAI-ARIA-Combobox, UX_KONZEPT.md §2), aber generisch
 * über {wert, label}-Optionen. Gefiltert wird im GESAMTEN Optionstext
 * (substring, ohne Groß-/Kleinschreibung), nicht nur am Anfang.
 */
import { useEffect, useMemo, useRef, useState } from "react"

export interface FilterOption {
  wert: string
  label: string
}

export interface FilterSelectProps {
  optionen: FilterOption[]
  /** "" = keine Auswahl */
  wert: string
  onChange: (wert: string) => void
  /** Platzhalter im Eingabefeld, z. B. "— wählen —" */
  placeholder?: string
  disabled?: boolean
  required?: boolean
}

export function FilterSelect({
  optionen,
  wert,
  onChange,
  placeholder = "Tippen zum Filtern…",
  disabled = false,
  required = false,
}: FilterSelectProps) {
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const listboxId = useRef(`filter-select-${Math.random().toString(36).slice(2)}`).current

  const selected = useMemo(() => optionen.find((o) => o.wert === wert) ?? null, [optionen, wert])

  // Eingabefeld zeigt den gewählten Wert, solange die Liste geschlossen ist.
  useEffect(() => {
    if (!open) setQuery(selected ? selected.label : "")
  }, [selected, open])

  const gefiltert = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("de")
    if (!q) return optionen
    return optionen.filter((o) => o.label.toLocaleLowerCase("de").includes(q))
  }, [optionen, query])

  useEffect(() => {
    if (activeIdx >= gefiltert.length) setActiveIdx(Math.max(0, gefiltert.length - 1))
  }, [gefiltert.length, activeIdx])

  // Schließen bei Klick außerhalb — stellt den letzten gültigen Wert wieder her.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery(selected ? selected.label : "")
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [open, selected])

  function auswaehlen(o: FilterOption) {
    onChange(o.wert)
    setQuery(o.label)
    setOpen(false)
    inputRef.current?.blur()
  }

  function leeren() {
    onChange("")
    setQuery("")
    inputRef.current?.focus()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setOpen(true)
      setActiveIdx((i) => Math.min(gefiltert.length - 1, i + 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIdx((i) => Math.max(0, i - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const opt = gefiltert[activeIdx]
      if (opt) auswaehlen(opt)
    } else if (e.key === "Escape") {
      setOpen(false)
      setQuery(selected ? selected.label : "")
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={
            open && gefiltert[activeIdx] ? `${listboxId}-opt-${activeIdx}` : undefined
          }
          value={query}
          disabled={disabled}
          required={required && !wert}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            setActiveIdx(0)
          }}
          onKeyDown={handleKeyDown}
          className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60 disabled:bg-slate-100"
        />
        {wert !== "" && !disabled && (
          <button
            type="button"
            aria-label="Auswahl leeren"
            onClick={leeren}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            ✕
          </button>
        )}
      </div>

      {open && !disabled && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          {gefiltert.length === 0 && (
            <div className="p-3 text-sm text-slate-500">Keine Treffer.</div>
          )}
          {gefiltert.map((o, idx) => (
            <button
              key={o.wert}
              id={`${listboxId}-opt-${idx}`}
              type="button"
              role="option"
              aria-selected={o.wert === wert}
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => auswaehlen(o)}
              className={`block w-full px-3 py-1.5 text-left text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
                idx === activeIdx ? "bg-slate-100" : "hover:bg-slate-50"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
